import { modelsOfProvider } from "@rally/agreed";
import type { BrandName, IconName } from "@rally/ui-kit";
import { say, type Word } from "../localization/say.ts";

export const PROVIDERS_THAT_TAKE_A_KEY = [
  "anthropic",
  "openai",
  "openrouter",
  "venice",
  "openai-compatible",
] as const;
export type ProviderThatTakesAKey = (typeof PROVIDERS_THAT_TAKE_A_KEY)[number];

export const PROVIDERS_THAT_SIGN_IN = ["anthropic", "openai-codex"] as const;

export type ProviderCardSubject = {
  provider: string;
  name: string;

  mark: BrandName | null;

  icon?: IconName;

  nameKey?: Word;
  whatKey: Word;
  signsIn: boolean;
  signInAs?: string;
  signInLabel?: string;
  takesAKey: boolean;

  needsAnEndpoint?: boolean;

  whereTheKeyIs: string | null;
};

export const PROVIDER_CARDS: readonly ProviderCardSubject[] = [
  {
    provider: "anthropic",
    name: "Anthropic",
    mark: "anthropic",
    whatKey: "provider.anthropicWhat",
    signsIn: true,
    signInLabel: "Claude",
    takesAKey: true,
    whereTheKeyIs: "console.anthropic.com → API keys",
  },
  {
    provider: "openai",
    name: "OpenAI",
    mark: "openai",
    whatKey: "provider.openaiWhat",
    signsIn: true,
    signInAs: "openai-codex",
    signInLabel: "ChatGPT",
    takesAKey: true,
    whereTheKeyIs: "platform.openai.com → API keys",
  },
  {
    provider: "openrouter",
    name: "OpenRouter",
    mark: "openrouter",
    whatKey: "provider.openrouterWhat",
    signsIn: false,
    takesAKey: true,
    whereTheKeyIs: "openrouter.ai → Keys",
  },
  {
    provider: "venice",
    name: "Venice",
    mark: "venice",
    whatKey: "provider.veniceWhat",
    signsIn: false,
    takesAKey: true,
    whereTheKeyIs: "venice.ai → API keys",
  },
  {
    provider: "openai-compatible",
    name: "OpenAI-compatible",
    nameKey: "provider.ownName",
    mark: null,
    icon: "hard-drive",
    whatKey: "provider.ownWhat",
    signsIn: false,
    takesAKey: true,
    needsAnEndpoint: true,
    whereTheKeyIs: null,
  },
];

export function theCard(provider: string): ProviderCardSubject | undefined {
  return PROVIDER_CARDS.find((one) => one.provider === provider || one.signInAs === provider);
}

export function holdsCredentialsOf(card: ProviderCardSubject): readonly string[] {
  return card.signInAs ? [card.provider, card.signInAs] : [card.provider];
}

export function providerCalled(provider: string): string {
  const card = theCard(provider);
  if (!card) return provider;
  const name = card.nameKey ? (say(card.nameKey) as string) : card.name;
  return card.signInAs === provider && card.signInLabel ? `${name} · ${card.signInLabel}` : name;
}

export function carriesItsOwnEndpoint(provider: string): boolean {
  return provider === "openai-compatible";
}

export function modelsOnOffer(credential: { provider: string; model?: string | null }): readonly string[] {
  if (carriesItsOwnEndpoint(credential.provider)) return credential.model ? [credential.model] : [];
  return modelsOfProvider(credential.provider).map((one) => one.id);
}

export function howManyModels(provider: string): number {
  return modelsOfProvider(provider).length;
}

export const A_KEY_IS_AT_LEAST = 16;

export function looksPasteable(secret: string, provider?: string): boolean {
  const floor = provider && carriesItsOwnEndpoint(provider) ? 1 : A_KEY_IS_AT_LEAST;
  return secret.length >= floor && secret.length <= 4096 && !/\s/.test(secret);
}

export function looksLikeABaseUrl(said: string): boolean {
  const trimmed = said.trim();
  return /^https?:\/\/[^\s?#]+$/.test(trimmed) && trimmed.length <= 512;
}

export function looksLikeACode(code: string): boolean {
  const said = code.trim();
  return said.length >= 8 && said.length <= 4096 && !/\s/.test(said);
}
